/**
 * Compound annual growth rate: the constant yearly rate that would take a value from
 * its beginning amount to its ending amount over the same time, with growth compounding.
 */

/** CAGR = (end ÷ begin)^(1 ÷ years) − 1, as a decimal. Needs positive values and years. */
export function cagr(begin: number, end: number, years: number): number | null {
  if (!(begin > 0 && end > 0 && years > 0)) {
    return null;
  }
  if (![begin, end, years].every(Number.isFinite)) {
    return null;
  }
  return (end / begin) ** (1 / years) - 1;
}

/** The value after `years` of growth at `rate` (a decimal) a year. */
export function project(begin: number, rate: number, years: number): number {
  return begin * (1 + rate) ** years;
}

/**
 * Years for `begin` to reach `target` at `rate` a year, or null when it never will
 * (no growth, or growth in the wrong direction) or an input is unusable.
 */
export function yearsToReach(
  begin: number,
  target: number,
  rate: number
): number | null {
  if (!(begin > 0 && target > 0) || rate <= -1 || rate === 0) {
    return null;
  }
  const years = Math.log(target / begin) / Math.log(1 + rate);
  return Number.isFinite(years) && years >= 0 ? years : null;
}

/** Years for a value to double at `rate` a year; null unless the rate is positive. */
export function doublingYears(rate: number): number | null {
  return rate > 0 ? Math.log(2) / Math.log(1 + rate) : null;
}

/** Total growth over the whole period as a decimal: end ÷ begin − 1. */
export function totalGrowth(begin: number, end: number): number {
  return end / begin - 1;
}

export interface PathPoint {
  year: number;
  value: number;
}

/**
 * The smooth path from the beginning value: one point per year (or every few years for
 * a long span, so there are at most `maxPoints`), always ending at `years` exactly.
 */
export function growthPath(
  begin: number,
  rate: number,
  years: number,
  maxPoints = 60
): PathPoint[] {
  const step = Math.max(1, Math.ceil(years / maxPoints));
  const points: PathPoint[] = [];
  for (let year = 0; year < years; year += step) {
    points.push({ year, value: project(begin, rate, year) });
  }
  points.push({ year: years, value: project(begin, rate, years) });
  return points;
}

/** What the reader wants to find. */
export type GrowthMode = "rate" | "future" | "years";

export interface GrowthInput {
  begin: number | null;
  end: number | null;
  /** The yearly rate as a decimal. */
  rate: number | null;
  years: number | null;
}

export interface GrowthSolution {
  begin: number;
  end: number;
  rate: number;
  years: number;
}

export type GrowthError =
  | "missing"
  | "bad-value"
  | "bad-years"
  | "bad-rate"
  | "same-value"
  | "unreachable";

export const MAX_YEARS = 1000;

function needs(mode: GrowthMode, input: GrowthInput): GrowthError | null {
  const { begin, end, rate, years } = input;
  if (begin === null || (mode !== "future" && end === null)) {
    return "missing";
  }
  if (mode !== "years" && years === null) {
    return "missing";
  }
  if (mode !== "rate" && rate === null) {
    return "missing";
  }
  if (begin <= 0 || (mode !== "future" && (end ?? 0) <= 0)) {
    return "bad-value";
  }
  if (
    mode !== "years" &&
    !(years !== null && years > 0 && years <= MAX_YEARS)
  ) {
    return "bad-years";
  }
  if (mode !== "rate" && (rate ?? 0) <= -1) {
    return "bad-rate";
  }
  return null;
}

/** Fill in the missing quantity, or say why it cannot be found. */
export function solveGrowth(
  mode: GrowthMode,
  input: GrowthInput
): GrowthSolution | GrowthError {
  const problem = needs(mode, input);
  if (problem) {
    return problem;
  }
  const begin = input.begin as number;
  if (mode === "rate") {
    const end = input.end as number;
    const years = input.years as number;
    return { begin, end, years, rate: cagr(begin, end, years) as number };
  }
  if (mode === "future") {
    const rate = input.rate as number;
    const years = input.years as number;
    return { begin, rate, years, end: project(begin, rate, years) };
  }
  const end = input.end as number;
  const rate = input.rate as number;
  if (begin === end) {
    return "same-value";
  }
  const years = yearsToReach(begin, end, rate);
  if (years === null || years > MAX_YEARS) {
    return "unreachable";
  }
  return { begin, end, rate, years };
}
